import { Component, Event, h, Prop, Listen, State, EventEmitter, Fragment, Env, Watch } from '@stencil/core';
import { setupFeatureFlags } from '../../../services';
import { DropdownItem } from '../dropdown-list/dropdown-list';
import { STORAGE_KEY } from '../../../constants';
import { emitSegmentGroupEvent, emitSegmentIdentifyEvent, emitSegmentTrackEvent, validateSegmentInstance } from '../../../services/segment';
import userflow from 'userflow.js';
import dd from '../../../services/datadog-rum';

export interface IHeaderSelections {
  organization: any;
  product: any;
  instance: any;
}

export interface IOrganization {
  Id: string;
  Name: string;
  SsoEnabled: string;
  MfaEnabled: string;
  CrmId: string;
}

export interface IUser {
  email: string;
  name: string;
  preferred_username?: string;
}

@Component({
  tag: 'opti-common-header',
  styleUrl: 'opti-common-header.scss',
  shadow: true,
})
export class OptiCommonHeader {
  emitReady: boolean = true;

  /** (deprecated) */
  @Prop() exclude: string = '';
  /** (optional) Excludes all components in the Global Bar. Only the Optimizely logo and name will appear. */
  @Prop({ attribute: 'exclude-all' }) excludeAll: boolean = false;
  /** (optional) Excludes the Product Switcher.  The Optimizely name will be displayed instead. */
  @Prop({ attribute: 'exclude-product-switcher' }) excludeProdSwitcher: boolean = false;
  /** (optional) Excludes the Profile icon and dropdown menu (which includes the logout button). */
  @Prop({ attribute: 'exclude-profile' }) excludeProfile: boolean = false;
  /** (optional) Excludes the Support icon and menu. */
  @Prop({ attribute: 'exclude-support' }) excludeSupport: boolean = false;
  /** (optional) Includes the Profile icon and dropdown menu (which includes the logout button) even when not authenticated.  */
  @Prop({ attribute: 'include-profile' }) includeProfile: boolean = false;
  /** (optional) The url that the user should be navigated to when they click the logout button within the user menu. Note that you will need to handle any other auth states within your application (ie this prop solely navigates the user to the url provided). */
  @Prop({ attribute: 'logout-url' }) logoutUrl: string = '';
  /** (optional) Allows setting a light theme (light bar).  */
  @Prop() theme: 'light' | '' = '';
  @Prop({ attribute: 'disable-navigation' }) disableNavigation: boolean = false;
  /** (optional) Initial organization id if using client side logic will look for this organization instead of the home organization as default. **/
  @Prop({ attribute: 'organization-id' }) organizationId?: string;
  /** (optional) Manual loading state. **/
  @Prop({ attribute: 'loading' }) showLoading?: boolean;
  /** (required) Okta Auth token used by common header to populate the Product Switcher with user instance permissions. */
  @Prop({ attribute: 'auth-token' }) authToken: string = '';
  @Watch('authToken')
  async updateUser() {
    await this._getUser();
  }
  /** (required) Informs the opti-common-header which instance the user is currently accessing */
  @Prop({ attribute: 'instance-id' }) instanceId?: string;
  /** (optional) Makes the header sticky on the application. (When implementing, you should set this to true . It is provided as a prop for existing users to have time to adjust their applications with the new implementation.) */
  @Prop() sticky: boolean = false;
  /** (optional - strongly encouraged) Provides initial Organization name. Will get replaced once instancePermissions are retreived. */
  @Prop({ attribute: 'org-name' }) orgName: string;
  /** (optional - strongly encouraged) Provides initial Product (Instance) name. Will get replaced once instancePermissions are retreived. */
  @Prop({ attribute: 'product-name' }) productName: string;

  @State() profileMenuOpen: boolean = false;
  @State() user: IUser | null = null;
  @Watch('user')
  setupOptimizelyFeatureFlags(newValue: { email: string }) {
    if (newValue?.email) {
      try {
        setupFeatureFlags(this.user.email);
      } catch {
        console.warn('Loading Global Bar without Optimizely SDK');
      }
    }
  };
  @State() organization: IOrganization | null = null;
  @State() selections: IHeaderSelections = {
    organization: null,
    product: null,
    instance: null,
  };
  // cannot reliably track object updates using @Watch
  // trackedSegmentUserEmail tracks user changes in global bar
  @State() trackedSegmentUserEmail: string;
  @Watch('trackedSegmentUserEmail')
  segmentIdentifyTracking() {
    if (!!this.organization?.CrmId && this.trackedSegmentUserEmail === this.user?.email) {
      // This will also need to be set by the consuming app that has an existing instance of Segment
      // to avoid duplicate identity calls for the same user upon login/session renewal
      emitSegmentIdentifyEvent({
        organization: this.organization,
        user: this.user,
      });
    }
  }
  // cannot reliably track object updates using @Watch
  // trackedSegmentOrganizationCrmId tracks organization changes in global bar
  @State() trackedSegmentOrganizationCrmId: string;
  @Watch('trackedSegmentOrganizationCrmId')
  segmentGroupTracking() {
    if (!!this.organization?.CrmId && this.trackedSegmentOrganizationCrmId === this.organization.CrmId) {
      emitSegmentGroupEvent(this.organization);
    }
  }

  @Event({
    eventName: '@opti-common-header:ready',
    bubbles: true,
  })
  onReady: EventEmitter<boolean>;

  @Event({
    eventName: '@opti-common-header:change',
    composed: true,
    bubbles: true,
    cancelable: true,
  })
  headerSelectionChange: EventEmitter<IHeaderSelections>;

  @Event({
    eventName: "@opti-common-header:error",
    composed: true,
    bubbles: true,
    cancelable: true
  })
  errorEvent: EventEmitter<{ type: string, location: string, details: any }>

  @Event({
    eventName: '@opti-common-header:logout',
    composed: true,
    bubbles: true,
    cancelable: true,
  })
  onLogout: EventEmitter;

  @Listen('opti-user-profile-toggle')
  onProfileToggle(event: CustomEvent<boolean>) {
    this.profileMenuOpen = event.detail;
  }

  @Listen('click', { target: 'window' })
  handleOnClick(event: any) {
    const eventElements = event.composedPath();
    // don't close if clicking inside the menu
    const hasUserMenuContent = eventElements.find(el => el.id === 'profile-dropdown-menu');
    if (this.profileMenuOpen && !hasUserMenuContent) {
      this.profileMenuOpen = false;
    }
  }

  @Listen('@opti-profile-dropdown:logout')
  onProfileLogout() {
    if (this.logoutUrl) {
      window.location.assign(this.logoutUrl);
    } else {
      this.onLogout.emit();
    }
  }

  @Listen('@opti-support-menu:select')
  async onSupportMenuSelection(event: CustomEvent<DropdownItem>) {
    const { text, href } = event.detail;
    const currentProductName = this.selections?.product?.value || this.selections?.instance?.valueObject?.ProductName;

    !!this.organization?.CrmId &&
      !!this.user?.email &&
      emitSegmentTrackEvent({
        customerId: this.organization?.CrmId,
        email: this.user?.email,
        eventName: 'Support Menu',
        data: {
          linkUrl: href,
          linkName: text,
          currentProductName,
        },
      });
  }

  @Listen('@opti-product-switcher:change')
  async onItemSelection(event: CustomEvent<DropdownItem[]>) {
    const items: DropdownItem[] = event.detail;
    const validItemTypes = ['instance', 'organization', 'product'];

    const completeSelectionUpdates = validItemTypes.map(async type => {
      if (type === 'organization') {
        const newOrgId = this.getNewOrgId(items);
        if (!!newOrgId) await this._getOrganizationDetails(newOrgId);
        // check and synchronize tracked and current user once instance and organization data is populated
        if (this.user?.email !== this.trackedSegmentUserEmail) this.trackedSegmentUserEmail = this.user?.email;
      }

      this.selections[type] = items.find(i => i.type === type);
    });

    await Promise.all(completeSelectionUpdates);

    this.headerSelectionChange.emit(this.selections);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.selections));
    // emit the on ready once the default storage is updated
    if (this.emitReady) {
      this.onReady.emit(true);
      this.emitReady = false;
    }
  }

  async _getUser() {
    if (!this.authToken) {
      return;
    }
    try {
      const endpoint = `${Env.AUTH_OIDC_ISSUER || ''}/v1/userinfo`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
        },
      });
      const user = await response.json();
      this.user = user;

      // only track and send segment user change event when organization and instance data is populated
      if (!!this.organization) this.trackedSegmentUserEmail = user.email;

      if (userflow && user) {
        userflow.init(Env.USERFLOW_TOKEN);

        userflow.identify(user.email, {
          name: user.name,
          email: user.email,
        });
      }
    } catch (e) {
      this.errorEvent.emit({
        type: "authentication",
        location: "getUser",
        details: e?.message || e
      });
      dd.addError(e);
      console.error(e);
      // if there's an error getting User info, set user to null so the Product Switcher doesn't get stuck in a loading state
      this.user = null;
    }
  }

  getNewOrgId(items) {
    const currentOrgId = items.find(i => i.type === 'organization')?.value;
    if (currentOrgId !== this.organization?.Id) return currentOrgId;
    return undefined;
  }

  async _getOrganizationDetails(orgId) {
    try {
      const response = await fetch(`${Env.IDENTITY_SERVICE_URL}/api/organizations/${orgId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const organization = await response.json();

      this.organization = organization;
      this.trackedSegmentOrganizationCrmId = organization.CrmId;
    } catch (e) {
      dd.addError(e);
      console.error(e);
    }
  }

  async componentWillLoad() {
    dd.startView("opti-common-header");
  }

  async componentWillRender() {
    await validateSegmentInstance();
    // populate user details if there is a session without a defined user object
    if (!!this.authToken && !this.user) await this._getUser();
  }

  render() {
    const { exclude, excludeAll, excludeProdSwitcher, excludeProfile, excludeSupport, includeProfile, theme } = this;
    const excludesAllComponents = excludeAll || exclude.includes('all');
    const isLightTheme = theme === 'light';
    const logo = isLightTheme
      ? 'https://optimizely-public-design-assets.s3.amazonaws.com/logos/Optimizely_Symbol_Full_Color.png'
      : 'https://optimizely-public-design-assets.s3.amazonaws.com/logos/Optimizely_Symbol_Mono_Light.png';

    const containerClasses = {
      'container': true,
      'container--light': isLightTheme,
      'container--sticky': this.sticky,
    };

    return (
      <div class={containerClasses}>
        <div class="logo">
          <img src={logo} alt={`${excludesAllComponents ? '' : 'Optimizely'}`} />
        </div>

        <div class="component-container">
          {excludesAllComponents || !(this.user || this.orgName) ? (
            <div class="name">Optimizely</div>
          ) : (
            <Fragment>
              {excludeProdSwitcher ? (
                <div class="name">Optimizely</div>
              ) : (
                <opti-product-switcher
                  auth-token={this.authToken}
                  selectionStorageKey={STORAGE_KEY}
                  user={this.user}
                  instanceId={this.instanceId}
                  orgName={this.orgName}
                  organization-id={this.organizationId}
                  customer-master-id={this.trackedSegmentOrganizationCrmId}
                  show-loading={this.showLoading}
                  disableNavigation={this.disableNavigation}
                  productName={this.productName}
                ></opti-product-switcher>
              )}
            </Fragment>
          )}
          {!excludesAllComponents && (
            <div class="system-components" part="system-components">
              <slot name="system-component"></slot>
              {!excludeSupport && <opti-support-menu authenticated={!!this.user}></opti-support-menu>}
              {(includeProfile || (!excludeProfile && this.user)) && <opti-profile-dropdown user={this.user}></opti-profile-dropdown>}
            </div>
          )}
        </div>
      </div>
    );
  }
}
